/**
 * 関連リンクの画面。
 *
 * 以前はスプレッドシートや別アプリを iframe で埋め込んでいたが、読み込みが重いうえ、
 * Google 側の制約で編集もできなかった。開く先を並べるだけにして、必要なときに
 * 別タブで開いてもらう。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 業務で開く外部のページ（スプレッドシート・別アプリ）へのリンクを並べる
 * @graph-connects none
 */
import type { TeamId } from '@/tasks/task';

/** リンク 1 件 */
export interface RelatedLink {
  /** 出す先のチーム */
  team: TeamId;
  /** リンクの名前 */
  name: string;
  /** 何のためのものか */
  description: string;
  /** 開く先 */
  url: string;
  /** 種類。アイコン代わりの短い印として出す */
  kind: 'シート' | 'アプリ';
}

/**
 * 業務で開く先の一覧。
 *
 * 増えてきたら、この定義を Firestore へ移して画面から足せるようにする。いまは
 * 数が少ないので、ここを唯一の定義にしておく
 * @graph-connects none
 */
export const RELATED_LINKS: readonly RelatedLink[] = [
  {
    team: 'sft',
    name: '月次決算管理用',
    description: '事業部から経理への売上・人件費報告',
    url: 'https://docs.google.com/spreadsheets/d/1I3r17-CPjjL8HUoKnvpUxaWawl_iIz5xYIuKkDsPlRg/edit?gid=1118105390#gid=1118105390',
    kind: 'シート',
  },
  {
    team: 'sft',
    name: 'P-004_経費リマインド',
    description: '経費・支払・勤怠の締め日。月ごとの案内日を決める元データ',
    url: 'https://docs.google.com/spreadsheets/d/1YAZilE8-1ZgmqVUQVvoBYLWHTCHSS_h8g8Sdh737sW0/edit?gid=0#gid=0',
    kind: 'シート',
  },
  {
    team: 'sft',
    name: '経理タスク＆スケジュール v3',
    description: '月次経理 FMT の出どころ。型そのものは ADO Hub の FMT で直せます',
    url: 'https://docs.google.com/spreadsheets/d/1SFsm_r8RyAaT0CXYxokGOWDPeyvd9mWUT-yYFIIzoww/edit?gid=9838404#gid=9838404',
    kind: 'シート',
  },
  {
    team: 'cpt',
    name: 'リーガルハブ',
    description: '法務の依頼・相談をまとめる別アプリ',
    url: 'https://legal-hub-962097537133.asia-northeast1.run.app/',
    kind: 'アプリ',
  },
  {
    team: 'cpt',
    name: 'PC 管理台帳',
    description: '貸与 PC の管理台帳',
    url: 'https://docs.google.com/spreadsheets/d/13wkwJSmz9sBkmuqkGieydP2-RPYtUq9ekfBrMFdVoaQ/edit?gid=446024558#gid=446024558',
    kind: 'シート',
  },
];

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business そのチームで出すリンクだけに絞る
 * @graph-connects none
 */
export function linksForTeam(team: TeamId): RelatedLink[] {
  return RELATED_LINKS.filter((link) => link.team === team);
}

/**
 * RelatedLinks の props。
 */
export interface RelatedLinksProps {
  /** 見ているチーム */
  team: TeamId;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 業務で開く外部のページを一覧にして、別タブで開けるようにする
 * @graph-connects linksForTeam [calls] チームごとのリンクを引く
 */
export function RelatedLinks({ team }: RelatedLinksProps) {
  const links = linksForTeam(team);

  return (
    <main className="min-w-0 flex-1 overflow-y-auto px-8 py-5">
      <header className="mb-4">
        <h1 className="m-0 text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
          関連リンク
        </h1>
        <p className="mt-0.5 mb-0 text-xs text-[var(--color-text-secondary)]">
          業務で開くスプレッドシートや別アプリ。押すと別タブで開きます
        </p>
      </header>

      {links.length === 0 ? (
        <p className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          このチームのリンクはまだありません
        </p>
      ) : (
        <ul className="m-0 max-w-2xl list-none space-y-2 p-0">
          {links.map((link) => (
            <li key={link.url}>
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-4 py-3 hover:bg-[var(--color-bg-subtle)]"
              >
                <span className="shrink-0 rounded bg-[var(--color-bg-subtle)] px-2 py-0.5 text-xs text-[var(--color-text-muted)]">
                  {link.kind}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-[var(--color-text-primary)]">
                    {link.name}
                  </span>
                  <span className="block text-xs text-[var(--color-text-secondary)]">
                    {link.description}
                  </span>
                </span>
                <span aria-hidden="true" className="shrink-0 text-[var(--color-text-muted)]">
                  ↗
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
